import { createPinia } from "pinia";
import { createApp } from "vue";

import * as Sentry from "@sentry/vue";

// FontAwesome injects its base CSS unlayered at runtime, which would override
// Tailwind v4 utilities (real @layer). The CSS is imported in styles/index.css
// inside @layer base instead, so utilities like h-6/h-full win again.
import { config as faConfig } from "@fortawesome/fontawesome-svg-core";
faConfig.autoAddCss = false;

import App from "./app.vue";

import GPortalUIkit from "@gportal/uikit";
import "@gportal/uikit/dist/styles/components/gp-brand-image.css";
import "./styles/index.css";
import "./styles/vueselect.css";

// Config
import config from "./config";

// i18n
import { i18n } from "./i18n";

// Router
import { router } from "./router";

// Auth
import { getSentryFingerprint } from "@/util/fingerprintedError";
import { initFeatureFlags, refreshContext } from "./featureflags";
import { auth } from "./keycloak";
import MarkdownItPlugin from "./plugins/markdownit";
import { useClientStore } from "./store/client";

const pinia = createPinia();
const app = createApp(App);

if (config.sentry) {
  const tracingOrigins: [string | RegExp] = [/^\//];
  if (config.backend.host) tracingOrigins.push(config.backend.host);
  Sentry.init({
    app,
    dsn: config.sentry.dsn,
    replaysSessionSampleRate: config.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.sentry.replaysOnErrorSampleRate,
    integrations: [
      Sentry.browserTracingIntegration({
        router: router,
        tracingOrigins,
      }),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [
          "http://127.0.0.1:8000/",
          "https://www.g-portal.com/ngpapi/",
          "https://www.g-portal.net/ngpapi/",
        ],
        unmask: config.sentry.unmask,
      }),
    ],
    environment: config.sentry.environment,
    tracesSampleRate: config.sentry.tracesSampleRate,
    beforeBreadcrumb: function (breadcrumb) {
      if (breadcrumb.category === "console") {
        // Filter out data array from console breadcrumbs to make them more readable
        breadcrumb.data = null;
      }
      if (breadcrumb.category === "fetch") {
        // Throw away redundant fetch breadcrumbs for GraphQL requests (use ones from CustomSentryLink instead)
        const url = breadcrumb.data?.url ?? "";
        if (url.match(new RegExp(config.backend.host + "/$"))) {
          return null;
        }
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      // Group FingerprintedErrors (util/fingerprintedError.ts) by their stable
      // context instead of the raw message: messages can embed variable data
      // (e.g. gRPC error reprs with timestamps), which created one Sentry
      // issue group per event (TV-1870). This check is intentionally generic —
      // it doesn't name CtxLoadError/SubscriptionError specifically, so any
      // future error class opts into safe grouping just by extending
      // FingerprintedError, with no change needed here.
      const fingerprint = getSentryFingerprint(hint.originalException);
      if (fingerprint) {
        // undefined means "not one of ours" — leave Sentry's own grouping alone.
        event.fingerprint = fingerprint;
      }
      return event;
    },
  });
}

app.use(pinia);
app.use(GPortalUIkit, {
  useToasts: true,
  toastOptions: {
    defaultTimeout: 3000,
  },
});
app.use(i18n);
app.use(router);
app.use(MarkdownItPlugin);

const client = useClientStore();

// Frequent server time update (with retries)
let serverTimeUpdateRetries = 1;

async function updateServerTimeOffset() {
  let delay = 600000; // 10 minute default delay
  console.debug("Synchronising server clock");
  try {
    await client.updateServerTimeOffset();
    serverTimeUpdateRetries = 1;
    console.debug("Synchronised server clock, next update in", delay / 1000, "s");
  } catch (_) {
    delay = 2000 * serverTimeUpdateRetries;
    serverTimeUpdateRetries += 1;
    console.error("Failed syncing server clock, retrying in", delay / 1000, "s");
  }
  setTimeout(() => updateServerTimeOffset(), delay);
}

updateServerTimeOffset().then(() => {});

auth.onAccessTokenChange = () => {
  client.setAuthToken(auth.accessToken);
  // Keep the feature-flag evaluation context in sync as the token refreshes.
  // No-op until the OpenFeature provider has initialised (fail-closed).
  void refreshContext();
};

auth.onAuthStatusChange = () => {
  client.setAuthStatus(auth.isAuthenticated);
  if (auth.isAuthenticated) {
    client.setAuthToken(auth.accessToken);
  }
};

auth
  .init({
    onLoad: "login-required",
  })
  .then(() => {
    app.mount("#app");
    // Fire-and-forget: feature flags fail closed, so mounting must not wait on
    // the relay. Runs after auth so the access token is available for the
    // evaluation context.
    void initFeatureFlags();
  });
